<template>
	<div v-if="users" class="report-page card-page">
		<content>
			<div class="report-wrapper">
				<header>
					<img
						src="@/assets/images/JA_logo.svg"
						alt="Junior Achievement Logo"
						class="ja-logo"
					/>
					<div class="ja-title">
						JA Finance Park <span class="trademark">&reg;</span>
					</div>
				</header>
				<h1 class="text--center mt--0">{{ $t('studentProgressTitle') }}</h1>
				<h3 class="text--center mt--0">
					{{ $tc('totalStudents', paginationData.recordsCount) }}
				</h3>
				<div class="filters-section">
					<h2 class="mb--0">{{ $t('selectedFilters') }}</h2>
					<div class="tag-group">
						<span
							v-for="filter in filters"
							v-show="!!filter.value"
							:key="filter.name"
							class="tag tag--lg"
						>
							{{ filter.label }}: {{ filter.name }}
						</span>
					</div>
				</div>
				<ja-expandable-reports-list
					:items="users"
					:life-stage="filters.lifeStage.value"
					@showSimulationAttempts="openSimAttemptsModal"
				></ja-expandable-reports-list>

				<ja-pagination
					:data-per-page="paginationData.recordsPerPage"
					:data-count="paginationData.recordsCount"
					@changePage="updatePagination($event)"
				/>
			</div>
		</content>
		<ja-info-modal
			v-if="isAttemptsModalVisible"
			:show="isAttemptsModalVisible"
			:title="$t('attemptsForSim', { name: simulationTries.modalTitle })"
			class="modal--large no-side-padding"
			:has-buttons="false"
			@closeModal="closeSimAttemptsModal"
		>
			<ja-simple-table
				:options="{
					columnKey: 'name',
					headerKey: 'try',
					dataKey: 'report',
					default: '-/-',
					firstCellText: 'Attempt'
				}"
				:items="simulationTries.report"
			></ja-simple-table>
		</ja-info-modal>
		<footer>
			<div class="logo-wrapper">
				<ja-local-sponsor
					v-if="localSponsor"
					:logo="localSponsor"
				></ja-local-sponsor>
			</div>
			<ja-button
				name="continue"
				class="small"
				@click.once.native="goToReporting"
				>{{ $t('close') }}</ja-button
			>
		</footer>
	</div>
</template>
<script>
import gql from 'graphql-tag';
import NavigationLoading from '@/mixins/NavigationLoading';
import AdminFacility from '@/mixins/AdminFacility';
import Button from '@/components/ui/Button';
import LocalSponsor from '@/components/ui/LocalSponsor';
import LocalSponsorService from '@/services/LocalSponsorService';
import '@/components/icons/checkmarks';
import '@/components/icons/arrow-right';
import { getStorageData } from '@/helpers/storageFunctions';
import StudentsService from '@/services/StudentsService';
import { ExpandableReportsList, SimpleTable } from '@ui';
import Pagination from '@/components/AdminSection/elements/Pagination';
import _ from 'lodash';
import InfoModal from '@/components/Modal/InfoModal.vue';

export default {
	components: {
		jaButton: Button,
		jaLocalSponsor: LocalSponsor,
		jaExpandableReportsList: ExpandableReportsList,
		jaPagination: Pagination,
		jaInfoModal: InfoModal,
		jaSimpleTable: SimpleTable
	},
	mixins: [NavigationLoading, AdminFacility],
	data() {
		return {
			filters: getStorageData('reportFilters') || null,
			lifeStages: null,
			localSponsor: null,
			currLifeStageDisplayOrder: null,
			users: [],
			isAttemptsModalVisible: false,
			simulationTries: {
				modalTitle: '',
				report: []
			},
			paginationData: {
				recordsPerPage: 15,
				currentPage: 1,
				recordsCount: 0
			}
		};
	},
	computed: {
		lifeStageFilters() {
			let lifeStageFilters = [];
			if (this.lifeStages) {
				this.lifeStages.forEach(lifeStage => {
					if (lifeStage.displayOrder >= this.currLifeStageDisplayOrder) {
						lifeStageFilters.push(`lifeStage_ID = "${lifeStage.lifeStage_ID}"`);
					}
				});
			}
			return lifeStageFilters;
		}
	},
	watch: {
		lifeStages(value) {
			const currLifeStageID = this.filters.lifeStage.value;
			this.currLifeStageDisplayOrder = value.filter(lifeStage => {
				return lifeStage.lifeStage_ID == currLifeStageID;
			})[0].displayOrder;
		},
		async lifeStageFilters(newStages) {
			if (newStages && newStages.length) {
				await this.getStudents();
			}
		}
	},
	mounted() {
		if (this.filters) {
			if (this.selectedFacilityId) {
				LocalSponsorService.getLogo(
					'StudentAtAGlance',
					this.selectedFacilityId
				).then(logo => (this.localSponsor = logo));
			}
		} else {
			this.goToReporting();
		}
	},
	methods: {
		goToReporting() {
			this.$router.push({ name: 'reporting' });
		},
		async updatePagination(page) {
			this.paginationData.currentPage = page || 1;
			this.isFetchInProgress = true;
			await this.getStudents();
		},
		getQueryFilters() {
			const filters = [];

			if (this.selectedFacilityId) {
				filters.push(`simulation.facility_ID = ${this.selectedFacilityId}`);
			}
			if (this.filters.simulationType && this.filters.simulationType.value) {
				filters.push(
					`simulation.simulationType= "${this.filters.simulationType.value}"`
				);
			}
			if (this.filters.school && this.filters.school.value) {
				filters.push(
					`simulation.school.Contains("${this.filters.school.value}")`
				);
			}
			if (this.filters.simulationClass.value) {
				filters.push(
					`simulation.sessionName= "${this.filters.simulationClass.value}"`
				);
			}

			if (this.filters.educator && this.filters.educator.value) {
				const educatorNames = this.filters.educator.value.split(' ');

				if (educatorNames.length == 1) {
					filters.push(
						`simulation.UserSimulations.Count(it.User.firstName.Contains("${this.filters.educator.value}") || it.User.lastName.Contains("${this.filters.educator.value}")) > 0`
					);
				} else {
					filters.push(
						`simulation.UserSimulations.Count(it.User.firstName.Contains("${educatorNames[0]}") && it.User.lastName.Contains("${educatorNames[1]}")) > 0`
					);
				}
			}

			filters.push('avatar != null');

			return filters;
		},

		async getStudents() {
			StudentsService.getUsersForReport(
				this.getQueryFilters(),
				this.lifeStageFilters,
				this.paginationData
			).then(({ data }) => {
				this.paginationData.recordsCount = data.usersCount;
				this.users = data.users.map(item => {
					return {
						...item,
						simulations: _.uniqBy(item.simulations, 'simulationId')
					};
				});

				this.$store.commit('setLoading', false);
			});
		},
		openSimAttemptsModal(simulation) {
			this.simulationTries.modalTitle = simulation.name;

			const { value: lifeStageId } = this.filters.lifeStage;

			StudentsService.getSimulationTriesFor(simulation, lifeStageId).then(
				response => {
					this.simulationTries.report = response;
				}
			);

			this.isAttemptsModalVisible = true;
		},
		closeSimAttemptsModal() {
			this.resetSimulationTries();
			this.isAttemptsModalVisible = false;
		},
		resetSimulationTries() {
			this.simulationTries.modalTitle = '';
			this.simulationTries.report = [];
		}
	},
	apollo: {
		lifeStages: {
			query: gql`
				{
					cmsLifeStages {
						name
						lifeStage_ID
						displayOrder
					}
				}
			`,
			fetchPolicy: 'no-cache',
			skip() {
				return !this.filters;
			},
			update: data => {
				return data.cmsLifeStages;
			}
		}
	}
};
</script>
<style lang="scss" scoped>
.report-page {
	width: 100%;

	content {
		width: 100%;
		display: flex;
		justify-content: center;

		.report-wrapper {
			height: 100%;
			width: 100%;
			max-width: 1000px;
		}

		header {
			display: flex;
			padding: 32px;
			justify-content: center;

			.ja-logo {
				height: 26px;
				display: inline-block;
			}
		}

		.filters-section {
			// 2x48px because 48px is the left/right padding
			width: calc(100% - 96px);
			padding-bottom: 16px;
			margin: 0 auto;
			border-bottom: 1px solid $container-border;
		}
	}

	footer {
		.logo-wrapper {
			height: 100%;
			padding: 4px 0;
			flex-grow: 1;
			img {
				height: 100%;
			}
		}
	}
}
</style>
